import { useState, createContext, useEffect } from "react";

export const AuthContext = createContext();

// Decodes JWT and builds the full user object.
// Called both at init (from localStorage) and whenever
// setAuthUserData is called after login.
function buildUserFromToken(token, permissionsEncoded) {
  if (!token) return null;
  try {
    const tokenData = JSON.parse(atob(token.split(".")[1]));
    const r = tokenData.result;

    let ePermissions = "";
    if (permissionsEncoded) {
      try {
        ePermissions = atob(permissionsEncoded);
      } catch (e) {}
    }

    const accepted =
      r.accepted_terms_and_conditions === "1" ||
      r.accepted_terms_and_conditions === 1
        ? 1
        : 0;

    console.log("accepted_terms_and_conditions: " + accepted);

    return {
      first_name: r.first_name ?? "",
      last_name: r.last_name ?? "",
      status: r.status ?? "",
      token: token,
      email: r.email ?? "",
      profile_photo_path: r.profile_photo_path ?? "",
      user_id: r.id ?? "",
      department_id: r.department?.id ?? "",
      designation_id: r.designation_id ?? "",
      department_name: r.department?.name ?? "",
      role_id: r.role_id ?? "",
      is_head: r.is_head ?? 0,
      is_admin: r.is_admin ?? 0,
      hr_head: r.hr_head ?? 0,
      permissions: ePermissions,
      accepted_terms_and_conditions: accepted,
    };
  } catch (error) {
    console.error("Error decoding token:", error);
    localStorage.removeItem("eToken");
    localStorage.removeItem("ePermissions");
    return null;
  }
}

export const AuthProvider = (props) => {
  // Build initial state synchronously from localStorage so that on
  // page refresh the full user object (including is_admin, department_id
  // etc.) is available immediately — before any effect fires.
  const initialToken = localStorage.getItem("eToken") ?? "";
  const initialPermissions = localStorage.getItem("ePermissions") ?? "";
  const initialUser = buildUserFromToken(initialToken, initialPermissions);

  const [authUserData, setAuthUserData] = useState(
    initialUser ? [initialUser] : [{ token: "" }],
  );

  // Whenever setAuthUserData is called (e.g. after login), re-decode
  // the token so ALL fields (is_admin, department_id, etc.) are always
  // present — regardless of what the login page passed in.
  useEffect(() => {
    const token = authUserData?.[0]?.token ?? "";
    if (!token) return;

    // If the token changed, re-decode it and merge the decoded fields
    // back into context. This fixes the login-redirect bug where
    // setAuthUserData was called with only partial user data.
    const permissions = localStorage.getItem("ePermissions") ?? "";
    const decoded = buildUserFromToken(token, permissions);
    if (!decoded) return;

    // Only update if something actually changed to avoid infinite loop
    const current = authUserData[0];
    if (
      current.is_admin !== decoded.is_admin ||
      current.department_id !== decoded.department_id ||
      current.is_head !== decoded.is_head ||
      current.permissions !== decoded.permissions
    ) {
      setAuthUserData([decoded]);
    }
  }, [authUserData?.[0]?.token]);

  return (
    <AuthContext.Provider value={[authUserData, setAuthUserData]}>
      {props.children}
    </AuthContext.Provider>
  );
};
